import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  FormFeedback,
} from "reactstrap";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { getEstablishments, addCompany } from "../../../slices/companies/thunk";

export type UserModalMode = "view" | "edit" | "add";

interface UserModalProps {
  isOpen: boolean;
  mode: UserModalMode;
  user: any;
  roles: any[];
  isSaving: boolean;
  establishment: any[];
  companies: any[];
  onClose: () => void;
  onSubmit: (payload: any) => void;
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  mode,
  user,
  roles,
  establishment,
  companies,
  isSaving,
  onClose,
  onSubmit,
}) => {
  const dispatch: any = useDispatch();
  const [formData, setFormData] = useState<any>({});
  const [passwordFieldError, setPasswordFieldError] = useState("");
  const [confirmPasswordFieldError, setConfirmPasswordFieldError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [emissionPointsForSelect, setEmissionPointsForSelect] = useState<any[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [createCompanyForAdmin, setCreateCompanyForAdmin] = useState<'yes' | 'no'>('no');
  const [changePassword, setChangePassword] = useState(false);

  const isView = mode === "view";
  const isAdd = mode === "add";
  const isEdit = mode === "edit";

  const validatePasswords = useCallback(() => {
    if (isEdit && !changePassword) {
      return { isValid: true, passErr: '', confirmErr: '' } as const;
    }

    const password = formData.password || '';
    const confirmPassword = formData.confirmPassword || '';

    if (!password) {
      return { isValid: false, passErr: 'La contraseña es requerida', confirmErr: '' } as const;
    }
    if (!confirmPassword) {
      return { isValid: false, passErr: '', confirmErr: 'Confirmar contraseña es requerida' } as const;
    }

    if (password !== confirmPassword) {
      return { isValid: false, passErr: '', confirmErr: 'Las contraseñas no coinciden' } as const;
    }

    return { isValid: true, passErr: '', confirmErr: '' } as const;
  }, [formData.password, formData.confirmPassword, isEdit, changePassword]);

  const selectedRoleName = useMemo(() => {
    if (!formData.roles || !roles) return "";
    const role = roles.find(r => (r.id || r._id) === formData.roles);
    return role ? role.name : "";
  }, [formData.roles, roles]);

  const companyOptions = useMemo(() => {
    if (!companies) return [];
    return companies.map((company: any) => ({
      value: company.id,
      label: company.businessName,
    }));
  }, [companies]);

  const selectedCompanyOption = useMemo(() => {
    if (!formData.companyId || formData.companyId === "" || !companyOptions.length) return null;
    return companyOptions.find(option => String(option.value) === String(formData.companyId)) || null;
  }, [formData.companyId, companyOptions]);

  const filteredEstablishments = useMemo(() => {
    if (!formData.companyId) return [];
    return establishment.filter((est: any) => {
        const estCompanyId = est.companyId || est.company?._id || est.company;
        return String(estCompanyId) === String(formData.companyId);
    });
  }, [formData.companyId, establishment]);

  const getEstablishmentId = (est: any) => {
    if (!est) return "";
    if (typeof est === "string") return est;
    if (typeof est === "object") return String(est.id || est._id || "");
    return "";
  };

  const getEmissionPointId = (ep: any) => {
    if (!ep) return "";
    if (typeof ep === "string") return ep;
    if (typeof ep === "object") return String(ep.id || ep._id || "");
    return "";
  };

  useEffect(() => {
      if (formData.companyId) {
          dispatch(getEstablishments(formData.companyId));
      }
  }, [dispatch, formData.companyId]);

  useEffect(() => {
      const estId = getEstablishmentId(formData.establishment);
      
      if (estId && establishment.length > 0) {
          const selectedEst = establishment.find((est: any) => String(est.id || est._id) === String(estId));
          
          if (selectedEst && selectedEst.emissionPoints && selectedEst.emissionPoints.length > 0) {
              setEmissionPointsForSelect(selectedEst.emissionPoints);
          } else {
              setEmissionPointsForSelect([]);
          }
      } else {
          setEmissionPointsForSelect([]);
      }
  }, [formData.establishment, establishment]);

  useEffect(() => {
    setPasswordFieldError("");
    setConfirmPasswordFieldError("");
    setFieldErrors({});
    if (isOpen) {
      if (mode === "add") {
        setFormData({});
        setEmissionPointsForSelect([]);
        setCreateCompanyForAdmin('no');
      } else if (user) {
        const userFormData: any = { ...user };
        let roleId = "";

        if (Array.isArray(user.roles) && user.roles.length > 0) {
          roleId = user.roles[0]?.id || user.roles[0]?._id || "";
        } else if (typeof user.roles === "object" && user.roles !== null) {
          roleId = user.roles.id || user.roles._id || "";
        }

        userFormData.roles = roleId;

        userFormData.companyId = user.company?.id || user.establishment?.companyId || user.companyId || user.company?._id || user.company || "";

        if (user.establishment && user.establishment.id) {
          userFormData.establishment = user.establishment.id;
        } else {
          const estId = getEstablishmentId(userFormData.establishment || userFormData.establishmentId);
          
          if (estId) {
            userFormData.establishment = estId;
          } else {
            userFormData.establishment = "";
          }
        }

        if (user.emissionPoint && user.emissionPoint.id) {
          userFormData.emissionPoint = user.emissionPoint.id;
        } else {
          const epId = getEmissionPointId(userFormData.emissionPoint || userFormData.emissionPointId);
          
          if (epId) {
            userFormData.emissionPoint = epId;
          } else {
            userFormData.emissionPoint = "";
          }
        }

        setFormData(userFormData);
      }
    } else {
      setFormData({});
      setEmissionPointsForSelect([]);
    }
  }, [isOpen, user, mode, establishment]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
  
    setFormData((prev: any) => ({ ...prev, [name]: value }));

    if (fieldErrors[name]) {
      setFieldErrors((prev: any) => ({ ...prev, [name]: '' }));
    }
  
    if (name === 'roles') {
      setCreateCompanyForAdmin('no');
    }
  
    
    if (name === "password") {
      setPasswordFieldError("");
    }
    if (name === "confirmPassword") {
      setConfirmPasswordFieldError("");
    }
  };

  useEffect(() => {
    if (!changePassword) {
      setFormData((prev: any) => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));
      setPasswordFieldError('');
      setConfirmPasswordFieldError('');
    }
  }, [changePassword]);

  const handleCompanyChange = (selectedOption: any) => {
    const companyId = selectedOption ? selectedOption.value : '';
    setFormData((prev: any) => ({
      ...prev,
      companyId: companyId,
      establishment: '',
      emissionPoint: ''
    }));
    setEmissionPointsForSelect([]);
  };

  const handleSave = async () => {
    // Validar campos obligatorios
    const errors: {[key: string]: string} = {};
    
    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'El nombre es obligatorio';
    }
    
    if (!formData.document || formData.document.trim() === '') {
      errors.document = 'El documento es obligatorio';
    }
    
    if (!formData.roles || formData.roles === '') {
      errors.roles = 'El rol es obligatorio';
    }
    
    // Si hay errores de campos obligatorios, mostrarlos y detener
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    
    // Limpiar errores de campos obligatorios
    setFieldErrors({});
    
    // Validar contraseñas
    const { isValid, passErr, confirmErr } = validatePasswords();
    if (!isValid) {
      setPasswordFieldError(passErr);
      setConfirmPasswordFieldError(confirmErr);
      return;
    }

    setPasswordFieldError('');
    setConfirmPasswordFieldError('');

    try {
      let finalCompanyId = formData.companyId;

      if (createCompanyForAdmin === 'yes' && (selectedRoleName === 'Admin' || selectedRoleName === 'Super Admin')) {
        const companyData = {
          businessName: formData.name || '',
          ruc: formData.document && formData.document.length === 13 ? formData.document : '9999999999999',
          address: 'Dirección por defecto - Actualizar después',
          obligedAccounting: true,
          environmentType: 1,
          email: formData.email || '',
          phone: '',
        };

        const companyResponse = await dispatch(addCompany(companyData)).unwrap();

        const companyId = companyResponse?.response?.id;
        
        if (companyId) {
          finalCompanyId = companyId;
        } else {
          console.error('No se pudo obtener el ID de la compañía creada');
          return;
        }
      }

      const userPayload = { ...formData };

      if (!isAdd && !changePassword) {
        delete userPayload.password;
        delete userPayload.confirmPassword;
      }

      if (mode === 'edit' && !userPayload.password) {
        delete userPayload.password;
      }

      if (userPayload.establishment) {
        userPayload.establishmentId = userPayload.establishment;
      }
      if (userPayload.emissionPoint) {
        userPayload.emissionPointId = userPayload.emissionPoint;
      }

      if (userPayload.roles) {
        userPayload.roles = [userPayload.roles];
      } else {
        delete userPayload.roles;
      }

      userPayload.companyId = finalCompanyId;

      await onSubmit(userPayload);

    } catch (error: any) {
      console.error('Error al crear usuario:', error);
    }
  };

  let title = "Detalles del Usuario";
  if (mode === "edit") title = "Editar Usuario";
  if (mode === "add") title = "Agregar Usuario";

  return (
      <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
        <ModalHeader toggle={onClose}>{title}</ModalHeader>
        <ModalBody>
          <Form>
            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label for="name">Nombre <span className="text-danger">*</span></Label>
                  <Input 
                    type="text" 
                    name="name" 
                    id="name" 
                    value={formData.name || ""} 
                    onChange={handleChange} 
                    disabled={isView}
                    invalid={!!fieldErrors.name}
                  />
                  {fieldErrors.name && <FormFeedback className="d-block">{fieldErrors.name}</FormFeedback>}
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup>
                  <Label for="document">Documento <span className="text-danger">*</span></Label>
                  <Input 
                    type="text" 
                    name="document" 
                    id="document" 
                    value={formData.document || ""} 
                    onChange={handleChange} 
                    disabled={isView}
                    invalid={!!fieldErrors.document}
                  />
                  {fieldErrors.document && <FormFeedback className="d-block">{fieldErrors.document}</FormFeedback>}
                  {fieldErrors.general && (
                    <div className="alert alert-danger mt-3" role="alert">
                      <i className="ri-error-warning-line me-2"></i>
                      <strong>Error:</strong> {fieldErrors.general}
                    </div>
                  )}
                </FormGroup>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label for="email">Correo electrónico</Label>
                  <Input type="email" name="email" id="email" value={formData.email || ""} onChange={handleChange} disabled={isView} invalid={!!fieldErrors.email} />
                  {fieldErrors.email && <FormFeedback className="d-block">{fieldErrors.email}</FormFeedback>}
                </FormGroup>
              </div>

              <div className="col-md-6">
                <FormGroup>
                  <Label for="roles">Rol <span className="text-danger">*</span></Label>
                  <Input 
                    type="select" 
                    name="roles" 
                    id="roles" 
                    value={formData.roles || ""} 
                    onChange={handleChange} 
                    disabled={isView}
                    invalid={!!fieldErrors.roles}
                  >
                    <option value="">Seleccione un rol</option>
                    {roles && roles.map((rol: any) => (
                        <option key={rol.id || rol._id} value={rol.id || rol._id}>
                          {rol.name}
                        </option>
                    ))}
                  </Input>
                  {fieldErrors.roles && <FormFeedback className="d-block">{fieldErrors.roles}</FormFeedback>}
                </FormGroup>
              </div>

              {(selectedRoleName === 'Admin' || selectedRoleName === 'Super Admin' || selectedRoleName === 'User') && (
                  <div className="col-12 mt-2">
                    <div className="card bg-light border-0">
                      <div className="card-body">

                        {(selectedRoleName === 'Admin' || selectedRoleName === 'Super Admin') && (
                            <FormGroup tag="fieldset">
                              <Label className="fw-bold">¿Desea crear una nueva compañía para este usuario?</Label>
                              <div className="d-flex gap-3 mt-2">
                                <FormGroup check>
                                  <Input
                                      name="createCompanyRadio"
                                      type="radio"
                                      checked={createCompanyForAdmin === 'no'}
                                      onChange={() => setCreateCompanyForAdmin('no')}
                                      disabled={isView}
                                  />
                                  {' '}
                                  <Label check>Asignar a una existente</Label>
                                </FormGroup>
                                <FormGroup check>
                                  <Input
                                      name="createCompanyRadio"
                                      type="radio"
                                      checked={createCompanyForAdmin === 'yes'}
                                      onChange={() => {
                                        setCreateCompanyForAdmin('yes');
                                        setFormData((prev: any) => ({ ...prev, companyId: "" }));
                                      }}
                                      disabled={isView}
                                  />
                                  {' '}
                                  <Label check>Crear una nueva</Label>
                                </FormGroup>
                              </div>
                            </FormGroup>
                        )}

                        {(selectedRoleName === 'User' ||
                            ((selectedRoleName === 'Admin' || selectedRoleName === 'Super Admin') && createCompanyForAdmin === 'no')) && (
                            <FormGroup className="mb-0 mt-3">
                              <Label for="companyId">Compañía</Label>
                              <Select
                                id="companyId"
                                name="companyId"
                                value={selectedCompanyOption}
                                onChange={handleCompanyChange}
                                options={companyOptions}
                                placeholder="Buscar o seleccionar compañía..."
                                isDisabled={isView}
                                isClearable
                              />
                            </FormGroup>
                        )}
                      </div>
                    </div>
                  </div>
              )}

              <div className="col-md-6 mt-3">
                <FormGroup>
                  <Label for="establishment">Establecimiento</Label>
                  <Input 
                    type="select" 
                    name="establishment" 
                    id="establishment" 
                    value={getEstablishmentId(formData.establishment)} 
                    onChange={handleChange} 
                    disabled={isView || !formData.companyId}
                  >
                    <option value="">Seleccione un establecimiento</option>
                    {filteredEstablishments.map((est: any) => (
                        <option key={est.id || est._id} value={String(est.id || est._id)}>
                          {est.code}
                        </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>

              <div className="col-md-6 mt-3">
                <FormGroup>
                  <Label for="emissionPoint">Punto de Emision</Label>
                  <Input
                      type="select"
                      name="emissionPoint"
                      id="emissionPoint"
                      value={getEmissionPointId(formData.emissionPoint)}
                      onChange={handleChange}
                      disabled={isView || !formData.establishment || emissionPointsForSelect.length === 0}
                  >
                    <option value="">Seleccione un punto de emisión</option>
                    {emissionPointsForSelect.map((ep: any) => (
                        <option key={ep.id || ep._id} value={String(ep.id || ep._id)}>
                          {ep.code}
                        </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
            </div>

            {(isAdd || isEdit) && (
                <div className="mt-3">

                  {isEdit && (
                      <div className="form-check form-switch mb-3">
                        <Input
                            className="form-check-input"
                            type="switch"
                            id="changePassword"
                            checked={changePassword}
                            onChange={() => setChangePassword(!changePassword)}
                        />
                        <Label className="form-check-label" for="changePassword">
                          Cambiar contraseña
                        </Label>
                      </div>
                  )}

                  {(isAdd || changePassword) && (
                      <div className="row">
                        <div className="col-md-6">
                          <FormGroup>
                            <Label for="password">
                              Contraseña <span className="text-danger">*</span>
                            </Label>

                            <div className="position-relative">
                              <Input
                                  type={showPassword ? "text" : "password"}
                                  name="password"
                                  id="password"
                                  value={formData.password || ""}
                                  onChange={handleChange}
                                  invalid={!!passwordFieldError}
                                  required={isAdd || changePassword}
                                />

                              <button
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2 p-0"
                                  tabIndex={-1}
                              >
                                <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                              </button>
                            </div>
                            {passwordFieldError && <FormFeedback className="d-block">{passwordFieldError}</FormFeedback>}
                          </FormGroup>
                        </div>

                        <div className="col-md-6">
                          <FormGroup>
                            <Label for="confirmPassword">Confirmar Contraseña <span className="text-danger">*</span></Label>

                            <div className="position-relative">
                              <Input
                                  type={showConfirmPassword ? "text" : "password"}
                                  name="confirmPassword"
                                  id="confirmPassword"
                                  value={formData.confirmPassword || ""}
                                  onChange={handleChange}
                                  invalid={!!confirmPasswordFieldError}
                                  required={isAdd || changePassword}
                                />

                              <button
                                  type="button"
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2 p-0"
                                  tabIndex={-1}
                              >
                                <i className={showConfirmPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                              </button>
                            </div>
                            {confirmPasswordFieldError && <FormFeedback className="d-block">{confirmPasswordFieldError}</FormFeedback>}
                          </FormGroup>
                        </div>
                      </div>
                  )}

                </div>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={onClose} disabled={isSaving}>
            Cerrar
          </Button>
          {!isView && (
              <Button color="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <><Spinner size="sm" className="me-2" /> Guardando...</> : (isAdd ? "Crear Usuario" : "Guardar Cambios")}
              </Button>
          )}
        </ModalFooter>
      </Modal>
  );
};

export default UserModal;
